import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as RPEvent } from "react";
import { Check } from "lucide-react";
import { Alert, Button } from "@/ui";
import {
  BRAND_ASPECTS,
  CROP_MIN_SOURCE_EDGE,
  SQUARE_ASPECT,
  SQUARE_ONLY_HINT,
  cropRegionDataUrl,
  type BrandAspect,
  type CropRegion,
} from "@/lib/imageCrop";

export interface PendingPick {
  file: File;
  url: string;
}

function fitFrame(ratio: number, iw: number, ih: number): CropRegion {
  if (iw <= 0 || ih <= 0) return { x: 0, y: 0, w: 1, h: 1 };
  if (iw / ih >= ratio) {
    const w = (ratio * ih) / iw;
    return { x: (1 - w) / 2, y: 0, w, h: 1 };
  }
  const h = iw / (ratio * ih);
  return { x: 0, y: (1 - h) / 2, w: 1, h };
}

type Handle = "move" | "nw" | "ne" | "sw" | "se";

const HANDLES: { id: Exclude<Handle, "move">; pos: Record<string, number>; cursor: string }[] = [
  { id: "nw", pos: { top: -8, left: -8 }, cursor: "nwse-resize" },
  { id: "ne", pos: { top: -8, right: -8 }, cursor: "nesw-resize" },
  { id: "sw", pos: { bottom: -8, left: -8 }, cursor: "nesw-resize" },
  { id: "se", pos: { bottom: -8, right: -8 }, cursor: "nwse-resize" },
];

export function LogoCropDialog({
  pending,
  onCancel,
  onCrop,
  ratioLocked = false,
}: {
  pending: PendingPick | null;
  onCancel: () => void;
  onCrop: (ratio: number, dataUrl: string) => void;
  /**
   * Hide the 16:9 option. Favicon crops are always stored square by the API, so
   * offering a wide frame here would let the user pick something that gets
   * thrown away on save.
   */
  ratioLocked?: boolean;
}) {
  const [ratio, setRatio] = useState(1);
  const [region, setRegion] = useState<CropRegion>({ x: 0, y: 0, w: 1, h: 1 });
  const [imgW, setImgW] = useState(0);
  const [imgH, setImgH] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef<{ handle: Handle; sx: number; sy: number } | null>(null);

  useEffect(() => {
    if (!pending) return;
    setRatio(1);
    setProblem(null);
    imgRef.current = null;
    setImgW(0);
    setImgH(0);
    setRegion({ x: 0, y: 0, w: 1, h: 1 });
  }, [pending]);

  const onLoad = useCallback((el: HTMLImageElement) => {
    if (el.naturalWidth < CROP_MIN_SOURCE_EDGE || el.naturalHeight < CROP_MIN_SOURCE_EDGE) {
      setProblem(
        `That image is ${el.naturalWidth}x${el.naturalHeight}. Use one at least ${CROP_MIN_SOURCE_EDGE}px on the short side.`,
      );
      setImgW(0);
      setImgH(0);
      return;
    }
    imgRef.current = el;
    setImgW(el.naturalWidth);
    setImgH(el.naturalHeight);
    setRegion(fitFrame(ratio, el.naturalWidth, el.naturalHeight));
  }, [ratio]);

  const onError = () => {
    setProblem("That image could not be read. Try a PNG or JPEG.");
    setImgW(0);
    setImgH(0);
  };

  const pickAspect = (id: BrandAspect) => {
    const next = BRAND_ASPECTS.find((a) => a.id === id)?.ratio ?? 1;
    if (next === ratio) return;
    setRatio(next);
    if (imgW > 0 && imgH > 0) setRegion(fitFrame(next, imgW, imgH));
  };

  const beginDrag =
    (handle: Handle) => (e: RPEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = { handle, sx: e.clientX, sy: e.clientY };
    };

  const onPointerMove = (e: RPEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const stage = stageRef.current;
    if (!drag || !stage) return;
    const rect = stage.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    // Convert once through the stage, in pixels. The old version normalised
    // x and y through rect.width and rect.height separately, so a fixed
    // pixel drag mapped to different proportional amounts per axis and the
    // 24px minimums clamped the two axes at wildly different widths — 24/624
    // is 4% of a 624px box but 24/156 is 15% of a 156px box. On a 4:1
    // wordmark preview that made horizontal shrinking effectively
    // impossible.
    const dxPx = e.clientX - drag.sx;
    const dyPx = e.clientY - drag.sy;

    setRegion((r) => {
      if (drag.handle === "move") {
        const minX = r.x * rect.width;
        const minY = r.y * rect.height;
        const maxX = Math.max(minX, rect.width - r.w * rect.width);
        const maxY = Math.max(minY, rect.height - r.h * rect.height);
        return {
          ...r,
          x: Math.max(minX, Math.min(maxX, minX + dxPx)) / rect.width,
          y: Math.max(minY, Math.min(maxY, minY + dyPx)) / rect.height,
        };
      }

      // Resize. The drag direction of the handle picks which axis of the
      // frame is being stretched: horizontal handles change the width,
      // vertical ones change the height, and the other dimension is derived
      // through ratio so the frame can never end up at a different aspect
      // ratio than the picker asked for.
      //
      // The old version applied the NW-corner transform to SE and vice versa,
      // which inverted the drag direction for three of the four handles:
      // SE dragged left instead of right, and the NW handle moved the whole
      // frame without resizing.
      const isEast = drag.handle === "se" || drag.handle === "ne";
      const isSouth = drag.handle === "se" || drag.handle === "sw";

      // The fixed corner of the frame (the one that stays put while only
      // the handle's own corner moves). px/py are displacements from that
      // anchor in the direction the frame grows.
      const dirX = isEast ? 1 : -1;
      const dirY = isSouth ? 1 : -1;
      const anchorPx = dirX > 0 ? r.x * rect.width : (1 - r.w) * rect.width;
      const anchorPy = dirY > 0 ? r.y * rect.height : (1 - r.h) * rect.height;

      // Growth available from the anchor to the stage edge, on each axis.
      const availW = dirX > 0 ? rect.width - anchorPx : anchorPx;
      const availH = dirY > 0 ? rect.height - anchorPy : anchorPy;

      // Dragging toward the anchor shrinks the frame; dragging away grows
      // it by the pointer travel, capped at the available space.
      const widthDelta = dirX > 0 ? dxPx : -dxPx;
      const heightDelta = dirY > 0 ? dyPx : -dyPx;

      // The larger travel wins; the other axis is derived from the chosen
      // one through ratio so the frame keeps its picked aspect exactly.
      const chosenW = Math.abs(widthDelta) >= Math.abs(heightDelta) / Math.max(ratio, 1e-9);
      const chosenDelta = chosenW ? widthDelta : heightDelta;

      // Clamp so the frame stays inside the stage and stays above the 24px
      // minimum on both axes. Both minimums are in screen pixels, which is
      // what the user's eye sees.
      const minW = 24;
      const minH = 24;
      let wPx: number;
      let hPx: number;
      if (chosenW) {
        wPx = Math.max(minW, Math.min(r.w * rect.width + widthDelta, r.w * rect.width + availW));
        hPx = wPx / ratio;
        if (hPx < minH) {
          hPx = minH;
          wPx = hPx * ratio;
        }
        if (wPx > r.w * rect.width + availW) {
          hPx = Math.min(hPx, r.h * rect.height + availH);
          wPx = hPx * ratio;
        }
      } else {
        hPx = Math.max(minH, Math.min(r.h * rect.height + heightDelta, r.h * rect.height + availH));
        wPx = hPx * ratio;
        if (wPx < minW) {
          wPx = minW;
          hPx = wPx / ratio;
        }
        if (hPx > r.h * rect.height + availH) {
          wPx = Math.min(wPx, r.w * rect.width + availW);
          hPx = wPx / ratio;
        }
      }

      wPx = Math.max(minW, Math.min(wPx, r.w * rect.width + availW));
      hPx = Math.max(minH, Math.min(hPx, r.h * rect.height + availH));

      const nx = dirX > 0 ? anchorPx : anchorPx - wPx;
      const ny = dirY > 0 ? anchorPy : anchorPy - hPx;
      return {
        x: Math.max(0, Math.min(1, nx / rect.width)),
        y: Math.max(0, Math.min(1, ny / rect.height)),
        w: Math.max(0, Math.min(1, wPx / rect.width)),
        h: Math.max(0, Math.min(1, hPx / rect.height)),
      };
    });
  };

  const endDrag = (e: RPEvent<HTMLDivElement>) => {
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // already released
    }
  };

  const apply = () => {
    if (!pending || !imgRef.current || imgW <= 0 || imgH <= 0) return;
    try {
      onCrop(ratio, cropRegionDataUrl(imgRef.current, region));
    } catch {
      setProblem("This browser could not crop that image.");
    }
  };

  if (!pending) return null;

  const ready = !problem && imgW > 0;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div aria-hidden="true" className="absolute inset-0 animate-fade bg-ink/45 backdrop-blur-[2px]" />
      <div
        aria-labelledby="logo-crop-title"
        aria-modal="true"
        className="relative flex w-full max-w-2xl animate-pop flex-col overflow-hidden rounded-lg border border-rule bg-raised shadow-pop"
        role="dialog"
        style={{ maxHeight: "90dvh" }}
      >
        <header className="shrink-0 border-b border-rule px-6 pb-4 pt-5">
          <h2 className="font-title text-lg leading-snug font-semibold text-ink" id="logo-crop-title">
            Crop this logo
          </h2>
          <p className="mt-1 text-sm text-muted">
            Pick a shape, then drag the frame&rsquo;s corners to keep the part that matters.
          </p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5 pt-4">
          <div className="mb-4 flex flex-wrap gap-2">
            {(ratioLocked ? [SQUARE_ASPECT] : BRAND_ASPECTS).map((a) => {
              const active = Math.abs(ratio - a.ratio) < 0.001;
              return (
                <button
                  className={`flex items-center gap-2.5 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                    active ? "border-brand bg-brand-light text-brand-ink" : "border-rule bg-raised text-ink hover:bg-raised/60"
                  }`}
                  key={a.id}
                  onClick={() => pickAspect(a.id)}
                  type="button"
                >
                  <span
                    aria-hidden="true"
                    className="block shrink-0 border-2"
                    style={{
                      aspectRatio: `${a.ratio}`,
                      borderColor: "currentColor",
                      height: 16,
                      width: 16 * a.ratio,
                    }}
                  />
                  <span className="min-w-0">
                    <span className="block font-medium leading-tight">{a.label}</span>
                    <span className="block text-xs text-muted">
                      {ratioLocked ? SQUARE_ONLY_HINT : a.hint}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {problem ? (
            <Alert kind="danger" title="Could not use that image">
              {problem}
            </Alert>
          ) : (
            <div className="flex justify-center">
              <div className="relative w-auto" ref={stageRef}>
                <img
                  alt=""
                  className="block max-h-[44vh] w-auto rounded-md"
                  draggable={false}
                  onLoad={(e) => onLoad(e.currentTarget)}
                  onError={onError}
                  src={pending.url}
                />
                {imgW > 0 ? (
                  <div
                    className="absolute border-2 border-brand shadow-pop"
                    style={{
                      left: `${region.x * 100}%`,
                      top: `${region.y * 100}%`,
                      width: `${region.w * 100}%`,
                      height: `${region.h * 100}%`,
                      cursor: "move",
                      touchAction: "none",
                    }}
                    onPointerDown={beginDrag("move")}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                  >
                    <span aria-hidden="true" className="absolute inset-0 border border-black/40" />
                    <span
                      aria-hidden="true"
                      className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-brand/40"
                    />
                    <span
                      aria-hidden="true"
                      className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-brand/40"
                    />
                    {HANDLES.map((h) => (
                      <span
                        aria-hidden="true"
                        className="absolute block rounded-[3px] border-2 border-brand bg-white"
                        key={h.id}
                        style={{ ...h.pos, height: 16, width: 16, cursor: h.cursor, touchAction: "none" }}
                        onPointerDown={beginDrag(h.id)}
                        onPointerMove={onPointerMove}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="absolute inset-0 grid place-items-center rounded-md text-sm text-muted">
                    Reading image…
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-rule bg-ink/[0.02] px-6 py-3.5">
          <Button onClick={onCancel} size="sm" variant="secondary">
            Cancel
          </Button>
          <Button disabled={!ready} icon={<Check aria-hidden="true" className="size-4" />} onClick={apply} size="sm">
            Use this crop
          </Button>
        </footer>
      </div>
    </div>
  );
}
